import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import { app } from '../firebase.js';
import { getFirestore, collection, orderBy, query, getDocs, addDoc, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate } from 'react-router-dom';

function OrdersComp() {
  const [ordersList, setOrdersList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [pricesList, setPricesList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDelivery, setSearchDelivery] = useState('');
  const [filteredPrices, setFilteredPrices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth(app);

  const db = getFirestore(app);

  const [formData, setFormData] = useState({
    orderType: 'Existing Client',
    clientId: '',
    newClientName: '',
    clientPhone: '',
    fromLocation: '',
    fromContact: '',
    fromContactPhone: '',
    toLocation: '',
    toPrice: 0,
    toDetails: '',
    toName: '',
    toContact: '',
    deliveryInstructions: ''
  });

  // Fetch orders, clients, prices
  useEffect(() => {
    const fetchOrders = async () => {
      const q = query(collection(db, 'Orders'), orderBy('timestamp', 'desc'));
      const snap = await getDocs(q);
      const orders = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrdersList(orders);
    };

    const fetchClients = async () => {
      const snap = await getDocs(collection(db, 'clients'));
      const clients = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClientsList(clients);
    };

    const fetchPrices = async () => {
      const snap = await getDocs(collection(db, 'prices'));
      const prices = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPricesList(prices);
      setFilteredPrices(prices);
    };

    fetchOrders();
    fetchClients();
    fetchPrices();
  }, [db]);

  // Search filter for delivery locations
  useEffect(() => {
    if (!searchDelivery) {
      setFilteredPrices(pricesList);
    } else {
      const lower = searchDelivery.toLowerCase();
      setFilteredPrices(
        pricesList.filter(
          (p) =>
            p.route.toLowerCase().includes(lower) ||
            p.location.toLowerCase().includes(lower)
        )
      );
    }
  }, [searchDelivery, pricesList]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectDelivery = (item) => {
    setFormData({
      ...formData,
      toLocation: item.location,
      toPrice: item.price
    });
    setSearchDelivery(item.location);
  };

  const resetForm = () => {
    setFormData({
      orderType: 'Existing Client',
      clientId: '',
      newClientName: '',
      clientPhone: '',
      fromLocation: '',
      fromContact: '',
      fromContactPhone: '',
      toLocation: '',
      toPrice: 0,
      toDetails: '',
      toName: '',
      toContact: '',
      deliveryInstructions: ''
    });
    setSearchDelivery('');
  };

  const validateOrderForm = () => {
    if (formData.orderType === 'New Client') {
      if (!formData.newClientName.trim() || !formData.clientPhone.trim()) {
        alert('Please fill in all client details');
        return false;
      }
    } else if (formData.orderType === 'Existing Client') {
      if (!formData.clientId) {
        alert('Please select an existing client');
        return false;
      }
    }

    if (!formData.fromLocation.trim() || !formData.fromContact.trim() || !formData.fromContactPhone.trim()) {
      alert('Please fill in all pickup details');
      return false;
    }

    if (!formData.toLocation.trim() || !formData.toName.trim() || !formData.toContact.trim()) {
      alert('Please fill in all delivery details');
      return false;
    }

    const phoneFields = [
      formData.clientPhone,
      formData.fromContactPhone,
      formData.toContact
    ];

    for (const phone of phoneFields) {
      if (!/^\d+$/.test(phone)) {
        alert('Phone numbers must contain only digits');
        return false;
      }
    }

    return true;
  };

  const buildOrderPayload = (selectedClient) => ({
    ...formData,
    clientUid: selectedClient?.uid || null,
    clientName:
      formData.orderType === 'Existing Client'
        ? selectedClient?.full_name || selectedClient?.businessName || ''
        : formData.newClientName,
    amount: formData.toPrice
  });

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateOrderForm()) return;

  setIsSubmitting(true);

  try {
    const selectedClient = clientsList.find((client) => client.id === formData.clientId);
    const createdByUid = auth.currentUser?.uid || null;

    await addDoc(collection(db, 'Orders'), {
      ...buildOrderPayload(selectedClient),
      timestamp: Timestamp.now(),
      status: 'Pending',
      createdByUid,
      createdByRole: 'admin'
    });

    setShowModal(false);
    resetForm();

    const q = query(collection(db, 'Orders'), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    const orders = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setOrdersList(orders);
  } catch (error) {
    console.error('Error placing order:', error);
  } finally {
    setIsSubmitting(false);
  }
};

const handleUpdate = async (e) => {
  e.preventDefault();
  if (!validateOrderForm()) return;
  if (!editingOrderId) return;

  setIsSubmitting(true);
  try {
    const selectedClient = clientsList.find((client) => client.id === formData.clientId);
    await updateDoc(doc(db, 'Orders', editingOrderId), {
      ...buildOrderPayload(selectedClient),
      updatedAt: Timestamp.now()
    });

    setShowModal(false);
    setEditingOrderId(null);
    resetForm();

    const q = query(collection(db, 'Orders'), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    const orders = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setOrdersList(orders);
  } catch (error) {
    console.error('Error updating order:', error);
  } finally {
    setIsSubmitting(false);
  }
};
 

  const viewOrder = (id) => {
    navigate(`/orders/${id}`);
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingOrderId(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (order) => {
    setModalMode('edit');
    setEditingOrderId(order.id);
    setFormData({
      orderType: order.orderType || 'Existing Client',
      clientId: order.clientId || '',
      newClientName: order.newClientName || '',
      clientPhone: order.clientPhone || '',
      fromLocation: order.fromLocation || '',
      fromContact: order.fromContact || '',
      fromContactPhone: order.fromContactPhone || '',
      toLocation: order.toLocation || '',
      toPrice: order.toPrice || order.amount || 0,
      toDetails: order.toDetails || '',
      toName: order.toName || '',
      toContact: order.toContact || '',
      deliveryInstructions: order.deliveryInstructions || ''
    });
    setSearchDelivery(order.toLocation || '');
    setShowModal(true);
  };

  const getOrderClientName = (order) => {
    if (order.orderType === 'Existing Client') {
      const client = clientsList.find(c => c.id === order.clientId);
      return client?.full_name || client?.businessName || '';
    }
    return order.newClientName || '';
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredOrders = ordersList.filter((order) => {
    if (!normalizedSearch) return true;
    const clientName = getOrderClientName(order);
    const values = [
      clientName,
      order.status,
      order.fromLocation,
      order.toLocation,
      order.amount,
      order.id
    ];
    return values.some((value) =>
      String(value || '').toLowerCase().includes(normalizedSearch)
    );
  });

  return (
    <div className="dataComp">
      <div className="dataCompTop">
        <h5>All Orders</h5>
        <div className="searchContainer">
          <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          <input
            type="text"
            placeholder="Type to search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button onClick={openCreateModal}>Place an Order</button>
        </div>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Client</th>
            <th>Status</th>
            <th>Pickup</th>
            <th>Delivery</th>
            <th>Amount</th>
            <th>Actions</th>
          </tr>
        </thead>
<tbody>
  {filteredOrders.map(order => {
    // Find client if existing
    const clientName = getOrderClientName(order);

    return (
      <tr key={order.id}>
        <td>{order.timestamp?.toDate().toLocaleString() || '-'}</td>
        <td>
          {clientName || 'N/A'}
        </td>
        <td style={{ color: order.status === 'Pending' ? 'red' : 'green' }}>
          {order.status}
        </td>
        <td>{order.fromLocation || '-'}</td>
        <td>{order.toLocation || '-'}</td>
        <td>{order.amount || 0}</td>
        <td>
          <button
            className="btn-sm btn btn-warning"
            onClick={() => viewOrder(order.id)}
          >
            View
          </button>
          <button
            className="btn-sm btn btn-info ms-2"
            onClick={() => openEditModal(order)}
          >
            Edit
          </button>
        </td>
      </tr>
    );
  })}
</tbody>
 
      </Table>

      {/* Place Order Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'edit' ? 'Edit Order' : 'Place a New Order'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={modalMode === 'edit' ? handleUpdate : handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Order Type</Form.Label>
              <Form.Select name="orderType" value={formData.orderType} onChange={handleChange}>
                <option value="Existing Client">Existing Client</option>
                <option value="New Client">New Client</option>
              </Form.Select>
            </Form.Group>

            {formData.orderType === 'Existing Client' ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Select Client</Form.Label>
                  <Form.Select
                    name="clientId"
                    value={formData.clientId}
                    onChange={(e) => {
                      const clientId = e.target.value;
                      const client = clientsList.find(c => c.id === clientId);
                      setFormData({
                        ...formData,
                        clientId,
                        clientPhone: client ? client.userphone : ''
                      });
                    }}
                  >
                    <option value="">Select Client</option>
                    {clientsList.map(client => (
                      <option key={client.id} value={client.id}>{client.full_name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Client Phone</Form.Label>
                  <Form.Control type="text" value={formData.clientPhone} disabled />
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>New Client Name</Form.Label>
                  <Form.Control type="text" name="newClientName" value={formData.newClientName} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Client Phone</Form.Label>
                  <Form.Control type="number" name="clientPhone" value={formData.clientPhone} onChange={handleChange} />
                </Form.Group>
              </>
            )}

            <h6>Pickup</h6>
            <Form.Group className="mb-3">
              <Form.Label>Pickup Location</Form.Label>
              <Form.Control type="text" name="fromLocation" value={formData.fromLocation} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control type="text" name="fromContact" value={formData.fromContact} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person Phone</Form.Label>
              <Form.Control type="number" name="fromContactPhone" value={formData.fromContactPhone} onChange={handleChange} />
            </Form.Group>

            <h6>Delivery</h6>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Location</Form.Label>
              <Form.Control
                type="text"
                value={searchDelivery}
                onChange={(e) => setSearchDelivery(e.target.value)}
                placeholder="Search delivery location"
              />
              {searchDelivery && (
                <div className="autocomplete-dropdown alert alert-info dropDCustom">
                  {filteredPrices.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectDelivery(item)}
                      className="dropDCustomitem"
                    >
                      {item.location} ({item.route}) - KES {item.price}
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Details (Floor, Door, etc)</Form.Label>
              <Form.Control type="text" name="toDetails" value={formData.toDetails} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Name</Form.Label>
              <Form.Control type="text" name="toName" value={formData.toName} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Contact</Form.Label>
              <Form.Control type="number" name="toContact" value={formData.toContact} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Instructions</Form.Label>
              <Form.Control type="text" name="deliveryInstructions" value={formData.deliveryInstructions} onChange={handleChange} />
            </Form.Group>

            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? modalMode === 'edit'
                  ? 'Saving...'
                  : 'Placing...'
                : modalMode === 'edit'
                  ? 'Save Changes'
                  : 'Place Order'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default OrdersComp;
